import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Fingerprint } from 'lucide-react'
import {
    passkeySignInUnavailableReason,
    preparePasskeySignIn,
    signInWithPasskey,
} from 'lib/passkeySignIn'
import { walletPasskeyErrorMessage } from 'lib/walletPasskeyWebAuthn'
import styles from './oauth-buttons.module.scss'

// Sign-in challenges last five minutes on the server; refresh a little sooner.
const OPTIONS_REFRESH_MS = 4 * 60 * 1000

// "Sign in with passkey". The request options are fetched ahead of the tap so the
// passkey sheet opens inside the click, which Safari requires.
export default function PasskeySignInButton({ onSignedIn, onError }) {
    const [ready, setReady] = useState(false)
    const [busy, setBusy] = useState(false)
    // Passkey sign-in ships with browser-wallet signup and is off until that is.
    const [enabled, setEnabled] = useState(false)
    const options = useRef(null)
    const unavailable = !enabled || passkeySignInUnavailableReason()

    useEffect(() => {
        let cancelled = false
        fetch(`${window.knovApiUrl}/api/v1/gon/signup`, { headers: { Accept: 'application/json' } })
            .then(response => (response.ok ? response.json() : null))
            .then(config => {
                if (!cancelled) setEnabled(config?.LOCAL_WALLET_SIGNUP === true)
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [])

    const refresh = useCallback(() => {
        options.current = null
        setReady(false)
        preparePasskeySignIn()
            .then(next => {
                options.current = next
                setReady(true)
            })
            .catch(() => setReady(false))
    }, [])

    useEffect(() => {
        if (unavailable) return undefined
        refresh()
        const timer = setInterval(refresh, OPTIONS_REFRESH_MS)
        // Timers sleep in background tabs, so a challenge may have expired meanwhile.
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh()
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            clearInterval(timer)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [refresh, unavailable])

    if (unavailable) return null

    const onClick = async () => {
        const prepared = options.current
        if (!prepared || busy) return
        options.current = null
        setBusy(true)
        try {
            const result = await signInWithPasskey(prepared)
            onSignedIn(result.token, result.data)
        } catch (error) {
            const message =
                error instanceof Error && error.name === 'NotAllowedError'
                    ? 'Passkey sign-in was cancelled.'
                    : walletPasskeyErrorMessage(error)
            onError(message)
            refresh()
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className={styles.oauthSection}>
            <button
                type="button"
                className={styles.oauthButton}
                onClick={onClick}
                disabled={!ready || busy}
                data-testid="passkey-sign-in-button"
            >
                <Fingerprint size={18} aria-hidden="true" />
                <span>{busy ? 'Signing in…' : 'Sign in with passkey'}</span>
            </button>
        </div>
    )
}
